import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { TextField, FormError, SubmitButton } from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setPending(true);
    try {
      await authApi.forgotPassword(email);
      setSent(true);
    } catch (e) {
      setErr(readApiError(e, t('auth.error_forgot_failed')));
    } finally {
      setPending(false);
    }
  }

  if (sent) {
    return (
      <AuthLayout title={t('auth.forgot_sent_title')}>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-start gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-emerald-600 text-white shrink-0">
              <Mail className="h-4 w-4" />
            </span>
            <div>
              <p className="text-[14px] text-emerald-900 font-medium">{t('auth.forgot_sent_body_title', { email })}</p>
              <p className="mt-1 text-[13px] text-emerald-800/85 leading-relaxed">
                {t('auth.forgot_sent_body')}
              </p>
            </div>
          </div>
        </div>
        <Link
          to="/login"
          className="mt-6 inline-flex items-center text-[13px] text-foreground hover:underline"
        >
          ← {t('auth.back_to_login')}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.forgot_title')} subtitle={t('auth.forgot_subtitle')}>
      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <TextField
          id="email"
          type="email"
          inputMode="email"
          label={t('auth.email_label')}
          placeholder="cfo@acme.co"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoFocus
          required
        />
        <SubmitButton pending={pending}>{t('auth.send_reset_link')}</SubmitButton>
      </form>

      <p className="mt-7 text-[13px] text-muted-foreground">
        <Link to="/login" className="hover:underline">← {t('auth.back_to_login')}</Link>
      </p>
    </AuthLayout>
  );
}
